import { useMutation } from '@tanstack/react-query';
import React, { useEffect, useState } from 'react';
import { Form, FormRenderProps } from 'react-final-form';
import { Trans } from 'react-i18next';
import { isConflictClientError, isUnauthorizedClientError } from '~domain/helper/errors';
import { EmailValidator } from '~domain/validators/core/basic-types/email-validator';
import { TextFormField } from '~frontend-common/components/form-fields';
import { ExternalLink, SentryErrorBox } from '~frontend-common/components/ui';
import { useCountdown } from '~frontend-common/hooks/use-countdown';
import { useTranslator } from '~frontend-common/i18n/translator';
import { Box } from '~ui-kit/components/box';
import { Button } from '~ui-kit/components/button/button';
import { Dialog, DialogActionButtonConfig } from '~ui-kit/components/dialog';
import { Typography } from '~ui-kit/components/typography';
import { authService, userService } from '~web-portal/services';
import { Links } from '~web-portal/variables/links';

interface EditEmailDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

interface FormValues {
  email?: string;
  passcode?: string;
}

type State = 'emailForm' | 'passcodeForm' | 'conflict';

export function EditEmailDialog({ isOpen, onClose }: EditEmailDialogProps) {
  const translator = useTranslator();
  const { tl } = translator;
  const [currentState, setCurrentState] = useState<State>('emailForm');
  const timer = useCountdown(30);

  // Request an OTP passcode to email, data will be the otp token to provide with the passcode
  const initiateOtpFlowMutation = useMutation({
    mutationFn: (email: string) =>
      authService.requestAccountModificationPasscode(email, translator.getCurrentLanguageId()),
  });

  // Validate the passcode that was sent to the user's email to get a special access token with adequate permissions
  // to modify the email
  const validateOtpMutation = useMutation({
    mutationFn: async ({ token, passcode }: { token: string; passcode: string }) => {
      try {
        const accessToken = await authService.validateEmailModificationPasscode(token, passcode);
        return {
          isValid: true,
          accessToken,
        } as const;
      } catch (e) {
        if (isUnauthorizedClientError(e)) {
          return { isValid: false } as const;
        }

        throw e;
      }
    },
  });

  // Modify the email using the special access token
  const updateEmailMutation = useMutation({
    mutationFn: ({ accessToken, email }: { accessToken: string; email: string }) => {
      return userService.updateEmail(accessToken, email);
    },
    onSuccess: () => onClose(),
    onError: (error: Error) => {
      if (isConflictClientError(error)) {
        setCurrentState('conflict');
      }
    },
  });

  useEffect(() => {
    // Properly reset the tasks when the dialog opens
    if (isOpen) {
      initiateOtpFlowMutation.reset();
      validateOtpMutation.reset();
      updateEmailMutation.reset();
      setCurrentState('emailForm');
    }
  }, [isOpen]);

  const handleRequestPasscode = (formValues: FormValues) => {
    // Request a passcode using the current email and display the passcode form
    setCurrentState('passcodeForm');
    timer.start();
    initiateOtpFlowMutation.mutate(formValues.email);
  };

  const handleValidateForm = (values: FormValues) => {
    const validator = new EmailValidator();
    const error = validator.validate(values.email);

    if (error) {
      return { email: tl(error as string) };
    }

    if (values.passcode.length !== 6) {
      return { passcode: tl('validation.error.required') };
    }
  };

  const handleSubmit = async (values: FormValues) => {
    const { email, passcode } = values;
    const token = initiateOtpFlowMutation.data;

    // When using `mutateAsync`, if the mutation throws an error, it will be propagated here, and we don't want to throw here.
    // So use a try/catch in order to absorb the error.
    // We could also add the `updateEmailMutation` in the `onSuccess` of the `validateOtpMutation`, but we would have to pass in the email.
    try {
      const result = await validateOtpMutation.mutateAsync({ token, passcode });

      if (result?.isValid) {
        updateEmailMutation.mutate({ accessToken: result.accessToken, email });
      }
    } catch (e) {
      // NO-OP
    }
  };

  const isResendInCooldown = timer.isRunning && timer.timeLeft > 0;

  return (
    <Form<FormValues>
      key={`${isOpen}`}
      initialValues={{ email: '', passcode: '' }}
      onSubmit={handleSubmit}
      validate={handleValidateForm}
      render={({ handleSubmit, errors, values }: FormRenderProps) => {
        const states: Record<State, { content: React.ReactNode; actions: DialogActionButtonConfig[] }> = {
          emailForm: {
            content: (
              <>
                <TextFormField
                  name="email"
                  label={tl('profile.editEmailDialog.fields.email.label')}
                  placeholder={tl('profile.editEmailDialog.fields.email.placeholder')}
                  variant="outlined"
                  required
                  disabled={initiateOtpFlowMutation.isLoading}
                  autoFocus
                />
              </>
            ),
            actions: [
              { label: tl('ui.navigation.cancel'), type: 'secondary', disabled: updateEmailMutation.isLoading },
              {
                label: tl('ui.navigation.next'),
                type: 'primary',
                buttonProps: {
                  type: 'submit',
                },
                onClick: () => handleRequestPasscode(values),
                disabled: Boolean(errors['email']),
                'data-id': 'btn-new-email-request-passcode',
              },
            ],
          },
          passcodeForm: {
            content: (
              <>
                <TextFormField
                  name="passcode"
                  label={tl('profile.editEmailDialog.fields.passcode.label', { fillers: [values?.email] })}
                  placeholder={tl('profile.editEmailDialog.fields.passcode.placeholder')}
                  variant="outlined"
                  disabled={validateOtpMutation.isLoading || updateEmailMutation.isLoading}
                  required
                  autoFocus
                  forceErrorMessage={
                    (validateOtpMutation.isError ||
                      (validateOtpMutation.isSuccess && !validateOtpMutation.data.isValid)) &&
                    tl('profile.editEmailDialog.fields.passcode.invalid')
                  }
                />

                <Box mt={3}>
                  <Button
                    appearance="none"
                    size="sm"
                    disabled={timer.isRunning && timer.timeLeft > 0}
                    onClick={() => handleRequestPasscode(values)}
                  >
                    <Typography muiProps={{ style: { textDecoration: 'underline' } }} noWrap>
                      {isResendInCooldown
                        ? tl('auth.passcodeForm.resendButton.cooldownLabel', { fillers: [timer.timeLeft] })
                        : tl('auth.passcodeForm.resendButton.readyLabel')}
                    </Typography>
                  </Button>
                </Box>

                {initiateOtpFlowMutation.isError && (
                  <SentryErrorBox error={initiateOtpFlowMutation.error} translator={translator} my={3} />
                )}

                {updateEmailMutation.isError && currentState !== 'conflict' && (
                  <SentryErrorBox error={updateEmailMutation.error} translator={translator} my={3} />
                )}
              </>
            ),
            actions: [
              { label: tl('ui.navigation.cancel'), type: 'secondary', disabled: updateEmailMutation.isLoading },
              {
                label: tl('profile.confirmEdit'),
                type: 'primary',
                buttonProps: {
                  type: 'submit',
                },
                disabled: initiateOtpFlowMutation.isLoading || Boolean(errors['passcode']),
                isLoading: validateOtpMutation.isLoading || updateEmailMutation.isLoading,
                onClick: handleSubmit,
                'data-id': 'btn-update-new-email',
              },
            ],
          },
          conflict: {
            content: (
              <Typography>
                <Trans
                  i18n={translator.i18n}
                  defaults={tl('profile.editEmailDialog.conflict.content')}
                  components={[
                    <ExternalLink to={Links.pages[translator.getCurrentLanguageId()].contactUs}>_</ExternalLink>,
                  ]}
                />
              </Typography>
            ),
            actions: [{ label: tl('profile.editEmailDialog.conflict.ok'), type: 'secondary' }],
          },
        };

        const state = states[currentState];

        return (
          <Dialog
            isOpen={isOpen}
            onClose={onClose}
            fullWidth
            maxWidth="sm"
            title={tl(`profile.editEmailDialog.title`)}
            wrapInForm
            formProps={{ onSubmit: handleSubmit }}
            actions={state?.actions}
          >
            {state?.content}
          </Dialog>
        );
      }}
    />
  );
}
